/* Local translation file — English + Bahasa Malaysia. No translation API (spec §11). */
import React, { createContext, useContext, useMemo, useState } from "react";

const STRINGS = {
  en: {
    appName: "RajinKerja",
    login: "Log in", email: "Email", password: "Password", loggingIn: "Logging in…",
    loginFailed: "Invalid email or password", accountLocked: "Account locked. Try again later.",
    changePassword: "Change password", currentPassword: "Current password",
    newPassword: "New password", confirmPassword: "Confirm new password",
    passwordRule: "At least 8 characters, with letters and numbers",
    passwordMismatch: "Passwords do not match", passwordChanged: "Password changed",
    passwordChangeRequired: "You must change your password before continuing",
    save: "Save", cancel: "Cancel", close: "Close", done: "Done", back: "Back", delete: "Delete",
    logout: "Log out",
    clockedIn: "Clocked in", clockedOut: "Clocked out",
    clockIn: "Clock in", clockOut: "Clock out",
    lastPunch: "Last punch", today: "Today", notEnrolled: "Face not enrolled yet — punches will need supervisor review",
    myHistory: "My history", hours: "Hours", date: "Date", inTime: "In", outTime: "Out",
    status: "Status", noHistory: "No punches in the last 30 days",
    cameraStarting: "Starting camera…",
    cameraDenied: "Camera permission denied. Open your browser settings and allow camera access for this site, then try again.",
    cameraDeniedIos: "On iPhone: open Settings → Safari (or your browser) → Camera, and set it to Allow. Then reload this page.",
    cameraUnavailable: "Camera is not available on this device or browser.",
    centerFace: "Centre your face in the oval", faceLooksGood: "Looks good — hold still",
    capture: "Capture", retake: "Retake", submit: "Submit", submitting: "Submitting…",
    gpsAcquiring: "Getting GPS…", gpsOk: "Location captured", gpsDenied: "Location denied",
    gpsUnavailable: "Location unavailable", gpsLowAccuracy: "Low GPS accuracy",
    gpsDeniedIosHint: "Location was denied. On iPhone: Settings → Privacy → Location Services → your browser → Allow. The punch will still be recorded.",
    summaryLabel: "Summary of today's work", summaryPlaceholder: "Optional — summary of today's work",
    punchRecorded: "Punch recorded", punchNeedsReview: "Recorded — needs supervisor review",
    punchFailMsg: "Your punch was recorded but the face did not match. A supervisor will review it.",
    punchNoFaceMsg: "Your punch was recorded but no face was detected. A supervisor will review it.",
    serverTime: "Server time", viewHistory: "View history",
    reviewQueue: "Review queue", pendingFirst: "Pending first", allPunches: "All punches", clockInOut: "Clock in / out",
    approve: "Approve", flag: "Flag", override: "Override", bulkApprove: "Approve selected",
    flagNoteRequired: "A note is required to flag", overrideNoteRequired: "Reason code and note are required",
    note: "Note", reasonCode: "Reason code", matchScore: "Match score", matchResult: "Match",
    location: "Location", accuracy: "Accuracy", coordinates: "Coordinates", photo: "Photo",
    pending: "Pending review", approved: "Approved", flagged: "Flagged", overridden: "Overridden",
    matchPass: "Match", matchFail: "No match", noFace: "No face detected", matchUnverified: "Unverified",
    facePendingVerifications: "awaiting face check",
    engineDownTitle: "Face verification engine is down",
    engineDownBody: "Staff can still clock in and out — their punches are saved as Unverified and will be verified automatically once the engine is back. Please contact support to restore it.",
    engineLoadingTitle: "Face verification engine is starting up",
    engineLoadingBody: "Punches made in the meantime are saved as Unverified and verified automatically in a minute or two.",
    engineAwaiting: "punch(es) awaiting verification",
    engineViewHealth: "View system health",
    engineLogTitle: "Face engine diagnostics (admin)",
    engineRunnerState: "Runner state", engineBundle: "Pre-built engine bundle",
    engineBundlePresent: "present — no install needed", engineBundleMissing: "MISSING — engine must install dependencies at boot",
    engineLogHint: "Copy this output when contacting support about the face engine.", facePendingHint: "Recorded while the face engine was down; they are re-verified automatically once it is back.",
    locOk: "Location captured", locDenied: "Location denied", locUnavailable: "Unavailable", locLowAccuracy: "Low accuracy",
    users: "Users", createUser: "Create user", editUser: "Edit user", fullName: "Full name",
    employeeId: "Employee ID", phone: "Phone", role: "Role", active: "Active", inactive: "Inactive",
    tempPassword: "Temporary password", resetPassword: "Reset password", deleteUser: "Delete user",
    deleteUserConfirm: "This permanently deletes the user AND destroys their face descriptor. Punch records are kept. This cannot be undone.",
    deactivate: "Deactivate", activate: "Activate", enrolFace: "Enrol face", reEnrolFace: "Re-enrol face",
    enrolled: "Enrolled", notEnrolledShort: "Not enrolled",
    consentOnFile: "Biometric consent on file (HR)", consentReference: "HR consent reference",
    consentDate: "Consent date", consentRequired: "Consent must be on file before enrolment",
    enrolInstructions: "Capture the staff member's reference photo. Make sure the face is well lit and centred.",
    enrolSuccess: "Face enrolled successfully", search: "Search", allRoles: "All roles",
    admin: "Admin", supervisor: "Supervisor", staff: "Staff",
    exportCsv: "Export CSV", auditLog: "Audit log", supervisorActivity: "Supervisor activity",
    action: "Action", actor: "Actor", target: "Target", when: "When", detail: "Detail",
    totalReviews: "Total reviews", approvals: "Approvals", flags: "Flags", overrides: "Overrides",
    medianSeconds: "Median secs to act", month: "Month", filter: "Filter", reset: "Reset",
    from: "From", to: "To", allStatuses: "All statuses", allResults: "All results", allStaff: "All staff",
    noResults: "No results", loading: "Loading…", reviewedBy: "Reviewed by", summary: "Summary",
    language: "Language", selectStaff: "Select staff",
    reasonDeviceFailure: "Device failure", reasonCameraUnavailable: "Camera unavailable",
    reasonGpsUnavailable: "GPS unavailable", reasonNotYetEnrolled: "Not yet enrolled",
    reasonAppearanceChanged: "Appearance changed", reasonPoorLighting: "Poor lighting",
    reasonSystemOutage: "System outage", reasonOther: "Other",
    welcomeBack: "Welcome back", signInToContinue: "Sign in to continue",
    punchesSelected: "selected", clockInLabel: "Clock in", clockOutLabel: "Clock out",
    notEnrolledPunchWarning: "You are not enrolled yet. Your punch will be recorded and reviewed by a supervisor.",
    lateDigest: "Late this morning", usualTime: "Usual clock-in", minLate: "min late",
    allClockedIn: "Everyone with a usual time has clocked in or is not yet due",
    clockedInCount: "clocked in", notYetDueCount: "not yet due",
    noHistoryCount: "no usual time yet", graceNote: "grace",
    photoRemoved: "Photo removed (retention policy)",
    minEarly: "min early", nearLabel: "near", openMap: "Open in Google Maps",
    allPunctuality: "Punctuality: all", lateClockIns: "Late clock-ins", earlyClockOuts: "Early clock-outs",
    zoomIn: "Zoom in", zoomOut: "Zoom out",
    lightboxHint: "Tap the photo to zoom \u00B7 scroll to pan when zoomed",
    locations: "Locations", locationsSubtitle: "Staff must pick one of these when clocking in or out.",
    locationName: "Location name", addLocation: "Add location", locActive: "Active",
    noLocations: "No locations defined yet. Staff can punch without selecting a location until one is added.",
    locationCreated: "Location added", locationDeleted: "Location deleted",
    deleteLocationTitle: "Delete this location?",
    deleteLocationNote: "existing punches keep the location name; staff will no longer see it in the dropdown.",
    locationLabel: "Location", selectLocation: "Select location",
    locationRequired: "Please select a location before submitting",
    editPunch: "Edit punch", correctedTime: "Corrected time (Malaysia time)",
    originalTime: "Original time", keepLocation: "Keep current location",
    editNoteLabel: "Note (optional)", editNotePlaceholder: "Reason for the correction…",
    saveChanges: "Save changes", punchUpdated: "Punch updated",
    editedBadge: "Edited",
    hoursSummaryTab: "Hours summary", byLocationTab: "By location",
    locationReportTitle: "Monthly manpower report",
    pickLocationHint: "Pick a location to generate its monthly manpower report.",
    manpower: "Manpower", manDays: "man-days", monthTotal: "Monthly total",
    monthLabel: "Month", dateLabel: "Date", printReportShort: "Print / PDF",
    renameUser: "Rename",
    exportPdf: "Export PDF", pdfExporting: "Preparing PDF...", pdfExported: "PDF downloaded",
    pdfExportFailed: "Could not generate the PDF — please try again",
    exportPdfHint: "The PDF contains the one-page summary with a sign-off block, followed by the supervisor activity trail and all supporting documents for this month.",
    enrolledReference: "Enrolled reference", punchSelfieLabel: "Punch selfie",
    allLocationsOverview: "All locations (overview)", daysActive: "Days active",
    inactiveLabel: "inactive",
    supervisorLocations: "Restrict to locations",
    supervisorLocationsHint: "This supervisor will only see punches, photos and reports for the ticked locations. Leave all unticked for full access.",
    shiftLabel: "Shift",
    shifts: "Shifts", createShift: "Create shift", editShift: "Edit shift",
    shiftName: "Shift name", startTime: "Start time", endTime: "End time",
    timeRange: "Time range", shiftKind: "Type", dayKind: "Day", nightKind: "Night",
    noShifts: "No shifts defined yet — create one to enable late/early detection",
    shiftsHelp: "Named time ranges. Punches auto-detect the nearest shift; overnight ranges are supported.",
    overnightHint: "If the end time is at or before the start time, the shift runs overnight into the next day.",
    deleteShift: "Delete shift", shiftDeleted: "Shift deleted",
    deleteShiftConfirm: "Past punches keep the shift name they recorded. Users assigned to this shift will fall back to automatic detection across all shifts.",
    assignedShifts: "Assigned shifts", anyShift: "No assignment — auto-detect across all active shifts",
    reports: "Reports", monthly: "Monthly", weekly: "Weekly", printReport: "Print for certification",
    attendanceSummary: "attendance summary", generatedAt: "Generated",
    daysWorkedShort: "Days", staffWhoWorked: "Staff who worked",
    lateMinutesShort: "Late (min)",
    cellHint: "each cell shows shift type and hours worked",
    cellClickHint: "click a cell to view and edit its clock in / out records",
    manualPunches: "Manual punches",
    manualPunchesHint: "Every clock in / out added manually by a supervisor or admin, newest first. Entries are also recorded in the audit log.",
    noManualPunches: "No manual entries yet",
    manualBadge: "Manual",
    addClockIn: "Add clock-in",
    addClockOut: "Add clock-out",
    manualEntryAdded: "Manual entry added — report updated",
    missingPunchHint: "Missing a punch?",
    noAttendanceDay: "No attendance recorded for this day",
    noAttendanceDayHint: "If the staff member did work (e.g. the face engine was down and nobody could clock in), add the full shift below with supporting evidence.",
    addFullShift: "Add full shift (clock-in + clock-out)",
    pickShiftToPrefill: "Pick a shift to pre-fill the times",
    saveFullShift: "Save both entries",
    saving: "Saving...",
    fullShiftAdded: "Clock-in and clock-out added — report updated",
    evidenceAttachedBoth: "attached to both entries",
    emptyDayClickHint: "click a blank day to add a missed shift",
    addedBy: "Added by",
    addedAt: "Added at",
    evidence: "Evidence",
    addEvidence: "Add Evidence",
    viewEvidence: "View evidence",
    evidenceAdded: "Evidence added",
    evidenceDeleted: "Evidence picture removed",
    deleteEvidenceTitle: "Remove this evidence picture?",
    deleteEvidenceBody: "The picture will be permanently deleted. This action is recorded in the audit log.",
    uploading: "Uploading…",
    evidenceOptional: "Evidence pictures (optional)",
    picturesSelected: "picture(s) selected",
    punchTimeLabel: "Punch time",
    punchType: "Type",
    next: "Next",
    certification: "Certification", certificationNote: "I certify that the attendance summary above has been reviewed and is correct.",
    certName: "Name", certDesignation: "Designation", certSignature: "Signature", certDate: "Date",
    pdpaTitle: "PDPA Biometric Consent", pdpaIntro: "Please read the notice below in full. Your acceptance and a live selfie will be stored as proof of consent.",
    pdpaAgreeLabel: "I have read and understood the notice above in English and Bahasa Malaysia, and I give my explicit consent to the collection and use of my biometric data as described.",
    pdpaSelfieNote: "Now capture a live selfie to record your acceptance:",
    pdpaAcceptButton: "Accept and record consent", pdpaRecorded: "PDPA consent recorded",
    pdpaStatus: "PDPA", pdpaAccepted: "Accepted", pdpaNotAccepted: "Not accepted",
    viewPdpaSelfie: "View PDPA acceptance", pdpaAcceptedOn: "Accepted on",
    pdpaRequiredEnrol: "This user must log in and accept the PDPA biometric notice before enrolment",
    loginHeadline: "Attendance, verified.", 
    loginSubline: "RajinKerja is your company's staff attendance system. Clock in and out from your phone — every punch is confirmed with a live selfie, face verification and GPS, then reviewed by your supervisor.",
    loginFeatureFace: "Face-verified punches — a live selfie is matched to your enrolled photo on the server",
    loginFeatureGps: "GPS-stamped records with the nearest named location",
    loginFeatureReview: "Supervisor-reviewed daily, with a full audit trail",
    loginFeaturePwa: "Installs to your home screen like an app — iPhone and Android",
    loginInternalNote: "Internal system. Accounts are created by your administrator.",
    faceEngineDown: "The face verification engine is not reachable. Ask your administrator to check the face-service container (see DEPLOYMENT.md \u00A7 Troubleshooting). Selfies cannot be verified until it is back.",
    faceEngineLoading: "The face verification engine is still starting up (loading models). Try again in about a minute.",
    systemHealth: "System health", refreshHealth: "Refresh",
    healthFaceEngine: "Face engine", healthDatabase: "Database", healthStorage: "Storage",
    healthOk: "Operational", healthLoading: "Starting up", healthDown: "Unreachable",
    healthFaceReady: "Models loaded — selfie verification ready",
    healthUsers: "users", healthPunches: "punches",
    healthFreeOf: "free of", healthPhotos: "photos", healthRetention: "retention",
    healthUnavailable: "Health status unavailable.",
    installAppTitle: "Get the app on your phone",
    installAppSubtitle: "Install RajinKerja on your home screen — free, no app store needed.",
    installIos: "iPhone / iPad", installAndroid: "Android",
    installIosTitle: "Install on iPhone / iPad",
    installIosStep1: "Open this website in Safari",
    installIosStep2: "Tap the Share button in the toolbar",
    installIosStep3: "Choose \u201CAdd to Home Screen\u201D, then tap Add",
    installAndroidTitle: "Install on Android",
    installAndroidStep1: "Open this website in Chrome",
    installAndroidStep2: "Tap the \u22EE menu button at the top right",
    installAndroidStep3: "Choose \u201CInstall app\u201D (or \u201CAdd to Home screen\u201D)",
    installDialogNote: "Takes less than a minute",
    installHint: "The app opens full screen from your home screen, just like a normal app.",
    installInstalled: "App installed — open RajinKerja from your home screen.",
    termsOfUse: "Terms of Use", privacyPolicy: "Privacy Policy",
    legalUpdated: "RajinKerja · Governed by the laws of Malaysia · PDPA 2010",
    deletePunch: "Delete punch",
    deletePunchTitle: "Delete this punch?",
    deletePunchBody: "The record is removed from reports, review queues and the staff member's history immediately. It is archived with your name and the time, and the deletion is written to the audit log.",
    punchDeleted: "Punch deleted",
    todayGlance: "Today at a glance",
    punchesToday: "Punches today", clockedInNow: "Clocked in now",
    lateArrivalsLabel: "Late arrivals", earlyDeparturesLabel: "Early departures",
    pendingReviewLabel: "Pending review",
    thisMonth: "This month",
    attendanceRate: "Attendance rate",
    attendanceRateHint: "Staff-days with a clock-in vs working days so far",
    onTimeRate: "On-time rate", onTimeRateHint: "Clock-ins without lateness",
    lateEventsLabel: "Late clock-ins", avgMinutesLateLabel: "Avg minutes late",
    activeStaffLabel: "active staff",
    faceVerification: "Face verification",
    facePassRate: "auto-verified", faceOfPunches: "of all punches this month",
    topLatecomers: "Top latecomers",
    noLatecomers: "No late clock-ins this month — great punctuality.",
    timesLate: "× late", totalMinLate: "total min late",
    punchesCol: "Punches", lateCol: "Late", pendingCol: "Pending",
    scopedNote: "Showing your assigned locations only",
    noDataMonth: "No punches recorded this month yet.",
    overridesNav: "Overrides",
    overridesHelp: "Every punch time and location edit, and every review decision — who did it, what changed, and why.",
    allEditors: "All supervisors", editTypeLabel: "Edit type", allTypes: "All types",
    typeTime: "Time edit", typeLocation: "Location edit", typeReview: "Review decision",
    noOverrides: "No overrides or edits recorded yet.",
    changeCol: "Change", prevPage: "Previous", nextPage: "Next",
    punchEditsTab: "Punch edits", roleChangesTab: "Role changes",
    changedBy: "Changed by", userCol: "User", createdAs: "Created as",
    noRoleChanges: "No role changes recorded yet.",
    weeklyTrend: "Weekly trend", last12Weeks: "last 12 weeks",
    recalcButton: "Recalculate late/early",
    recalcTitle: "Recalculate late & early minutes?",
    recalcBody: "Re-checks every punch from the last 180 days against the current shifts and staff assignments, and fixes any wrong late/early values (e.g. night shift punches matched to a day shift). Safe to run anytime.",
    recalcDone: "punches corrected", recalcScanned: "checked",
    mySummary: "My summary",
    daysWorked: "Days worked", onTimeDaysLabel: "On-time days", lateDaysLabel: "Late days",
    onTimeStreakLabel: "On-time streak", streakUnit: "consecutive on-time days",
    streakPraise: "Great going — keep it up!",
    streakStart: "Clock in on time to start a streak.",
    noSummaryMonth: "No punches recorded for this month.",
    legendOnTime: "On time", legendLate: "Late", dayByDay: "Day by day",
    totalMinEarly: "total min early",
  },
  bm: {
    appName: "RajinKerja",
    login: "Log masuk", email: "E-mel", password: "Kata laluan", loggingIn: "Sedang log masuk…",
    loginFailed: "E-mel atau kata laluan tidak sah", accountLocked: "Akaun dikunci. Cuba lagi nanti.",
    changePassword: "Tukar kata laluan", currentPassword: "Kata laluan semasa",
    newPassword: "Kata laluan baharu", confirmPassword: "Sahkan kata laluan baharu",
    passwordRule: "Sekurang-kurangnya 8 aksara, dengan huruf dan nombor",
    passwordMismatch: "Kata laluan tidak sepadan", passwordChanged: "Kata laluan ditukar",
    passwordChangeRequired: "Anda mesti menukar kata laluan sebelum meneruskan",
    save: "Simpan", cancel: "Batal", close: "Tutup", done: "Selesai", back: "Kembali", delete: "Padam",
    logout: "Log keluar",
    clockedIn: "Sudah masuk", clockedOut: "Sudah keluar",
    clockIn: "Masuk kerja", clockOut: "Keluar kerja",
    lastPunch: "Rekod terakhir", today: "Hari ini", notEnrolled: "Wajah belum didaftarkan — rekod akan disemak penyelia",
    myHistory: "Sejarah saya", hours: "Jam", date: "Tarikh", inTime: "Masuk", outTime: "Keluar",
    status: "Status", noHistory: "Tiada rekod dalam 30 hari lepas",
    cameraStarting: "Memulakan kamera…",
    cameraDenied: "Kebenaran kamera ditolak. Buka tetapan pelayar dan benarkan akses kamera untuk laman ini, kemudian cuba lagi.",
    cameraDeniedIos: "Di iPhone: buka Tetapan → Safari (atau pelayar anda) → Kamera, dan pilih Benarkan. Kemudian muat semula halaman ini.",
    cameraUnavailable: "Kamera tidak tersedia pada peranti atau pelayar ini.",
    centerFace: "Letakkan wajah anda di tengah bujur", faceLooksGood: "Bagus — kekal pegun",
    capture: "Tangkap", retake: "Ambil semula", submit: "Hantar", submitting: "Menghantar…",
    gpsAcquiring: "Mendapatkan GPS…", gpsOk: "Lokasi direkod", gpsDenied: "Lokasi ditolak",
    gpsUnavailable: "Lokasi tidak tersedia", gpsLowAccuracy: "Ketepatan GPS rendah",
    gpsDeniedIosHint: "Lokasi ditolak. Di iPhone: Tetapan → Privasi → Perkhidmatan Lokasi → pelayar anda → Benarkan. Rekod tetap akan disimpan.",
    summaryLabel: "Ringkasan kerja hari ini", summaryPlaceholder: "Pilihan — ringkasan kerja hari ini",
    punchRecorded: "Rekod disimpan", punchNeedsReview: "Disimpan — perlu semakan penyelia",
    punchFailMsg: "Rekod anda disimpan tetapi wajah tidak sepadan. Penyelia akan menyemaknya.",
    punchNoFaceMsg: "Rekod anda disimpan tetapi tiada wajah dikesan. Penyelia akan menyemaknya.",
    serverTime: "Masa pelayan", viewHistory: "Lihat sejarah",
    reviewQueue: "Senarai semakan", pendingFirst: "Menunggu dahulu", allPunches: "Semua rekod", clockInOut: "Masuk / keluar kerja",
    approve: "Lulus", flag: "Tanda", override: "Ganti", bulkApprove: "Lulus pilihan",
    flagNoteRequired: "Nota diperlukan untuk menanda", overrideNoteRequired: "Kod sebab dan nota diperlukan",
    note: "Nota", reasonCode: "Kod sebab", matchScore: "Skor padanan", matchResult: "Padanan",
    location: "Lokasi", accuracy: "Ketepatan", coordinates: "Koordinat", photo: "Foto",
    pending: "Menunggu semakan", approved: "Diluluskan", flagged: "Ditanda", overridden: "Diganti",
    matchPass: "Padan", matchFail: "Tidak padan", noFace: "Tiada wajah dikesan", matchUnverified: "Belum disahkan",
    facePendingVerifications: "menunggu semakan wajah",
    engineDownTitle: "Enjin pengesahan wajah tidak aktif",
    engineDownBody: "Staf masih boleh masuk dan keluar kerja — rekod mereka disimpan sebagai Belum disahkan dan akan disahkan secara automatik apabila enjin kembali. Sila hubungi sokongan untuk memulihkannya.",
    engineLoadingTitle: "Enjin pengesahan wajah sedang dimulakan",
    engineLoadingBody: "Rekod yang dibuat sementara itu disimpan sebagai Belum disahkan dan disahkan secara automatik dalam satu dua minit.",
    engineAwaiting: "rekod menunggu pengesahan",
    engineViewHealth: "Lihat kesihatan sistem",
    engineLogTitle: "Diagnostik enjin wajah (pentadbir)",
    engineRunnerState: "Status pelari", engineBundle: "Bundel enjin pra-bina",
    engineBundlePresent: "ada — tiada pemasangan diperlukan", engineBundleMissing: "TIADA — enjin perlu memasang kebergantungan semasa mula",
    engineLogHint: "Salin output ini apabila menghubungi sokongan mengenai enjin wajah.", facePendingHint: "Direkod semasa enjin wajah tidak aktif; disahkan semula secara automatik apabila ia kembali.",
    locOk: "Lokasi direkod", locDenied: "Lokasi ditolak", locUnavailable: "Tidak tersedia", locLowAccuracy: "Ketepatan rendah",
    users: "Pengguna", createUser: "Cipta pengguna", editUser: "Sunting pengguna", fullName: "Nama penuh",
    employeeId: "ID pekerja", phone: "Telefon", role: "Peranan", active: "Aktif", inactive: "Tidak aktif",
    tempPassword: "Kata laluan sementara", resetPassword: "Set semula kata laluan", deleteUser: "Padam pengguna",
    deleteUserConfirm: "Ini akan memadam pengguna secara kekal DAN memusnahkan deskriptor wajah mereka. Rekod kehadiran dikekalkan. Tindakan ini tidak boleh dibatalkan.",
    deactivate: "Nyahaktif", activate: "Aktifkan", enrolFace: "Daftar wajah", reEnrolFace: "Daftar semula wajah",
    enrolled: "Didaftarkan", notEnrolledShort: "Belum didaftarkan",
    consentOnFile: "Persetujuan biometrik dalam fail (HR)", consentReference: "Rujukan persetujuan HR",
    consentDate: "Tarikh persetujuan", consentRequired: "Persetujuan mesti ada dalam fail sebelum pendaftaran",
    enrolInstructions: "Tangkap foto rujukan pekerja. Pastikan wajah terang dan di tengah.",
    enrolSuccess: "Wajah berjaya didaftarkan", search: "Cari", allRoles: "Semua peranan",
    admin: "Pentadbir", supervisor: "Penyelia", staff: "Kakitangan",
    exportCsv: "Eksport CSV", auditLog: "Log audit", supervisorActivity: "Aktiviti penyelia",
    action: "Tindakan", actor: "Pelaku", target: "Sasaran", when: "Bila", detail: "Butiran",
    totalReviews: "Jumlah semakan", approvals: "Kelulusan", flags: "Tanda", overrides: "Gantian",
    medianSeconds: "Median saat bertindak", month: "Bulan", filter: "Tapis", reset: "Set semula",
    from: "Dari", to: "Hingga", allStatuses: "Semua status", allResults: "Semua keputusan", allStaff: "Semua kakitangan",
    noResults: "Tiada hasil", loading: "Memuatkan…", reviewedBy: "Disemak oleh", summary: "Ringkasan",
    language: "Bahasa", selectStaff: "Pilih kakitangan",
    reasonDeviceFailure: "Kerosakan peranti", reasonCameraUnavailable: "Kamera tidak tersedia",
    reasonGpsUnavailable: "GPS tidak tersedia", reasonNotYetEnrolled: "Belum didaftarkan",
    reasonAppearanceChanged: "Penampilan berubah", reasonPoorLighting: "Pencahayaan lemah",
    reasonSystemOutage: "Gangguan sistem", reasonOther: "Lain-lain",
    welcomeBack: "Selamat kembali", signInToContinue: "Log masuk untuk meneruskan",
    punchesSelected: "dipilih", clockInLabel: "Masuk kerja", clockOutLabel: "Keluar kerja",
    notEnrolledPunchWarning: "Anda belum didaftarkan. Rekod anda akan disimpan dan disemak oleh penyelia.",
    lateDigest: "Lewat pagi ini", usualTime: "Waktu masuk biasa", minLate: "minit lewat",
    allClockedIn: "Semua yang mempunyai waktu biasa telah masuk atau belum tiba masanya",
    clockedInCount: "sudah masuk", notYetDueCount: "belum tiba masa",
    noHistoryCount: "belum ada waktu biasa", graceNote: "tempoh ihsan",
    photoRemoved: "Foto dipadam (polisi penyimpanan)",
    minEarly: "minit awal", nearLabel: "berhampiran", openMap: "Buka di Google Maps",
    allPunctuality: "Ketepatan masa: semua", lateClockIns: "Masuk lewat", earlyClockOuts: "Keluar awal",
    zoomIn: "Zum masuk", zoomOut: "Zum keluar",
    lightboxHint: "Ketik foto untuk zum \u00B7 skrol untuk gerak apabila dizum",
    locations: "Lokasi", locationsSubtitle: "Kakitangan mesti memilih salah satu semasa masuk atau keluar kerja.",
    locationName: "Nama lokasi", addLocation: "Tambah lokasi", locActive: "Aktif",
    noLocations: "Tiada lokasi ditakrifkan lagi. Kakitangan boleh ketik tanpa memilih lokasi sehingga satu ditambah.",
    locationCreated: "Lokasi ditambah", locationDeleted: "Lokasi dipadam",
    deleteLocationTitle: "Padam lokasi ini?",
    deleteLocationNote: "ketikan sedia ada mengekalkan nama lokasi; kakitangan tidak lagi melihatnya dalam senarai.",
    locationLabel: "Lokasi", selectLocation: "Pilih lokasi",
    locationRequired: "Sila pilih lokasi sebelum menghantar",
    editPunch: "Sunting ketikan", correctedTime: "Masa diperbetulkan (waktu Malaysia)",
    originalTime: "Masa asal", keepLocation: "Kekalkan lokasi semasa",
    editNoteLabel: "Nota (pilihan)", editNotePlaceholder: "Sebab pembetulan\u2026",
    saveChanges: "Simpan perubahan", punchUpdated: "Ketikan dikemas kini",
    editedBadge: "Disunting",
    hoursSummaryTab: "Ringkasan jam", byLocationTab: "Ikut lokasi",
    locationReportTitle: "Laporan tenaga kerja bulanan",
    pickLocationHint: "Pilih lokasi untuk menjana laporan tenaga kerja bulanannya.",
    manpower: "Tenaga kerja", manDays: "hari-pekerja", monthTotal: "Jumlah bulanan",
    monthLabel: "Bulan", dateLabel: "Tarikh", printReportShort: "Cetak / PDF",
    renameUser: "Tukar nama",
    exportPdf: "Eksport PDF", pdfExporting: "Menyediakan PDF...", pdfExported: "PDF dimuat turun",
    pdfExportFailed: "PDF tidak dapat dijana — sila cuba lagi",
    exportPdfHint: "PDF ini mengandungi ringkasan satu halaman dengan ruang pengesahan, diikuti jejak tindakan penyelia dan semua dokumen sokongan untuk bulan ini.",
    enrolledReference: "Rujukan pendaftaran", punchSelfieLabel: "Selfie ketikan",
    allLocationsOverview: "Semua lokasi (ringkasan)", daysActive: "Hari aktif",
    inactiveLabel: "tidak aktif",
    supervisorLocations: "Hadkan kepada lokasi",
    supervisorLocationsHint: "Penyelia ini hanya akan melihat ketikan, foto dan laporan bagi lokasi yang ditanda. Biarkan semua tidak ditanda untuk akses penuh.",
    shiftLabel: "Syif",
    shifts: "Syif", createShift: "Cipta syif", editShift: "Sunting syif",
    shiftName: "Nama syif", startTime: "Masa mula", endTime: "Masa tamat",
    timeRange: "Julat masa", shiftKind: "Jenis", dayKind: "Siang", nightKind: "Malam",
    noShifts: "Tiada syif ditakrifkan — cipta satu untuk pengesanan lewat/awal",
    shiftsHelp: "Julat masa bernama. Rekod mengesan syif terdekat secara automatik; syif malam merentas tengah malam disokong.",
    overnightHint: "Jika masa tamat sama atau sebelum masa mula, syif berjalan hingga hari berikutnya.",
    deleteShift: "Padam syif", shiftDeleted: "Syif dipadam",
    deleteShiftConfirm: "Rekod lama mengekalkan nama syif yang direkodkan. Pengguna yang ditugaskan akan kembali kepada pengesanan automatik.",
    assignedShifts: "Syif ditugaskan", anyShift: "Tiada tugasan — kesan automatik merentas semua syif aktif",
    reports: "Laporan", monthly: "Bulanan", weekly: "Mingguan", printReport: "Cetak untuk pengesahan",
    attendanceSummary: "ringkasan kehadiran", generatedAt: "Dijana",
    daysWorkedShort: "Hari", staffWhoWorked: "Kakitangan yang bekerja",
    lateMinutesShort: "Lewat (min)",
    cellHint: "setiap sel menunjukkan jenis syif dan jam bekerja",
    cellClickHint: "klik sel untuk melihat dan mengubah rekod masuk / keluar",
    manualPunches: "Rekod manual",
    manualPunchesHint: "Semua rekod masuk / keluar yang ditambah secara manual oleh penyelia atau pentadbir, terbaru dahulu. Entri turut direkodkan dalam log audit.",
    noManualPunches: "Tiada entri manual lagi",
    manualBadge: "Manual",
    addClockIn: "Tambah masuk kerja",
    addClockOut: "Tambah keluar kerja",
    manualEntryAdded: "Entri manual ditambah — laporan dikemas kini",
    missingPunchHint: "Rekod tertinggal?",
    noAttendanceDay: "Tiada kehadiran direkodkan pada hari ini",
    noAttendanceDayHint: "Jika staf memang bekerja (cth. enjin wajah tidak aktif dan tiada sesiapa boleh masuk kerja), tambah syif penuh di bawah bersama bukti sokongan.",
    addFullShift: "Tambah syif penuh (masuk + keluar)",
    pickShiftToPrefill: "Pilih syif untuk mengisi masa secara automatik",
    saveFullShift: "Simpan kedua-dua entri",
    saving: "Menyimpan...",
    fullShiftAdded: "Masuk dan keluar kerja ditambah — laporan dikemas kini",
    evidenceAttachedBoth: "dilampirkan pada kedua-dua entri",
    emptyDayClickHint: "klik hari kosong untuk menambah syif yang tertinggal",
    addedBy: "Ditambah oleh",
    addedAt: "Ditambah pada",
    evidence: "Bukti",
    addEvidence: "Tambah Bukti",
    viewEvidence: "Lihat bukti",
    evidenceAdded: "Bukti ditambah",
    evidenceDeleted: "Gambar bukti dibuang",
    deleteEvidenceTitle: "Buang gambar bukti ini?",
    deleteEvidenceBody: "Gambar akan dipadam secara kekal. Tindakan ini direkodkan dalam log audit.",
    uploading: "Memuat naik…",
    evidenceOptional: "Gambar bukti (pilihan)",
    picturesSelected: "gambar dipilih",
    punchTimeLabel: "Masa rekod",
    punchType: "Jenis",
    next: "Seterusnya",
    certification: "Pengesahan", certificationNote: "Saya mengesahkan bahawa ringkasan kehadiran di atas telah disemak dan adalah betul.",
    certName: "Nama", certDesignation: "Jawatan", certSignature: "Tandatangan", certDate: "Tarikh",
    pdpaTitle: "Persetujuan Biometrik PDPA", pdpaIntro: "Sila baca notis di bawah sepenuhnya. Penerimaan anda dan swafoto langsung akan disimpan sebagai bukti persetujuan.",
    pdpaAgreeLabel: "Saya telah membaca dan memahami notis di atas dalam Bahasa Inggeris dan Bahasa Malaysia, dan memberikan persetujuan nyata terhadap pengumpulan dan penggunaan data biometrik saya seperti yang diterangkan.",
    pdpaSelfieNote: "Sekarang tangkap swafoto langsung untuk merekod penerimaan anda:",
    pdpaAcceptButton: "Terima dan rekod persetujuan", pdpaRecorded: "Persetujuan PDPA direkodkan",
    pdpaStatus: "PDPA", pdpaAccepted: "Diterima", pdpaNotAccepted: "Belum diterima",
    viewPdpaSelfie: "Lihat penerimaan PDPA", pdpaAcceptedOn: "Diterima pada",
    pdpaRequiredEnrol: "Pengguna ini mesti log masuk dan menerima notis biometrik PDPA sebelum pendaftaran",
    loginHeadline: "Kehadiran, disahkan.",
    loginSubline: "RajinKerja ialah sistem kehadiran kakitangan syarikat anda. Masuk dan keluar kerja dari telefon anda — setiap rekod disahkan dengan swafoto langsung, pengesahan wajah dan GPS, kemudian disemak oleh penyelia.",
    loginFeatureFace: "Rekod disahkan wajah — swafoto langsung dipadankan dengan foto pendaftaran anda di pelayan",
    loginFeatureGps: "Rekod bercap GPS dengan lokasi bernama terdekat",
    loginFeatureReview: "Disemak penyelia setiap hari, dengan jejak audit penuh",
    loginFeaturePwa: "Dipasang ke skrin utama seperti aplikasi — iPhone dan Android",
    loginInternalNote: "Sistem dalaman. Akaun dicipta oleh pentadbir anda.",
    faceEngineDown: "Enjin pengesahan wajah tidak dapat dihubungi. Minta pentadbir menyemak kontena face-service (lihat DEPLOYMENT.md \u00A7 Troubleshooting). Selfie tidak boleh disahkan sehingga ia kembali.",
    faceEngineLoading: "Enjin pengesahan wajah masih dimulakan (memuatkan model). Cuba lagi dalam seminit.",
    systemHealth: "Kesihatan sistem", refreshHealth: "Muat semula",
    healthFaceEngine: "Enjin wajah", healthDatabase: "Pangkalan data", healthStorage: "Storan",
    healthOk: "Beroperasi", healthLoading: "Sedang dimulakan", healthDown: "Tidak dapat dihubungi",
    healthFaceReady: "Model dimuatkan — pengesahan selfie sedia",
    healthUsers: "pengguna", healthPunches: "ketikan",
    healthFreeOf: "kosong daripada", healthPhotos: "foto", healthRetention: "penyimpanan",
    healthUnavailable: "Status kesihatan tidak tersedia.",
    installAppTitle: "Dapatkan aplikasi di telefon anda",
    installAppSubtitle: "Pasang RajinKerja pada skrin utama — percuma, tiada app store diperlukan.",
    installIos: "iPhone / iPad", installAndroid: "Android",
    installIosTitle: "Pasang pada iPhone / iPad",
    installIosStep1: "Buka laman web ini dalam Safari",
    installIosStep2: "Ketik butang Kongsi pada bar alat",
    installIosStep3: "Pilih \u201CTambah ke Skrin Utama\u201D, kemudian ketik Tambah",
    installAndroidTitle: "Pasang pada Android",
    installAndroidStep1: "Buka laman web ini dalam Chrome",
    installAndroidStep2: "Ketik butang menu \u22EE di penjuru kanan atas",
    installAndroidStep3: "Pilih \u201CInstall app\u201D (atau \u201CAdd to Home screen\u201D)",
    installDialogNote: "Mengambil masa kurang seminit",
    installHint: "Aplikasi dibuka skrin penuh dari skrin utama anda, seperti aplikasi biasa.",
    installInstalled: "Aplikasi telah dipasang — buka RajinKerja dari skrin utama anda.",
    termsOfUse: "Terma Penggunaan", privacyPolicy: "Dasar Privasi",
    legalUpdated: "RajinKerja · Tertakluk kepada undang-undang Malaysia · PDPA 2010",
    deletePunch: "Padam ketikan",
    deletePunchTitle: "Padam ketikan ini?",
    deletePunchBody: "Rekod ini dikeluarkan daripada laporan, baris gilir semakan dan sejarah staf serta-merta. Ia diarkibkan bersama nama anda dan masa, dan pemadaman direkodkan dalam log audit.",
    punchDeleted: "Ketikan dipadam",
    todayGlance: "Hari ini sepintas lalu",
    punchesToday: "Ketikan hari ini", clockedInNow: "Sedang bekerja",
    lateArrivalsLabel: "Masuk lewat", earlyDeparturesLabel: "Keluar awal",
    pendingReviewLabel: "Menunggu semakan",
    thisMonth: "Bulan ini",
    attendanceRate: "Kadar kehadiran",
    attendanceRateHint: "Hari-pekerja dengan masuk kerja berbanding hari setakat ini",
    onTimeRate: "Kadar tepat masa", onTimeRateHint: "Masuk kerja tanpa kelewatan",
    lateEventsLabel: "Masuk lewat", avgMinutesLateLabel: "Purata minit lewat",
    activeStaffLabel: "kakitangan aktif",
    faceVerification: "Pengesahan wajah",
    facePassRate: "disahkan automatik", faceOfPunches: "daripada semua ketikan bulan ini",
    topLatecomers: "Paling kerap lewat",
    noLatecomers: "Tiada masuk lewat bulan ini — ketepatan masa yang baik.",
    timesLate: "× lewat", totalMinLate: "jumlah minit lewat",
    punchesCol: "Ketikan", lateCol: "Lewat", pendingCol: "Menunggu",
    scopedNote: "Menunjukkan lokasi yang ditugaskan kepada anda sahaja",
    noDataMonth: "Tiada ketikan direkodkan bulan ini lagi.",
    overridesNav: "Pindaan",
    overridesHelp: "Setiap pindaan masa dan lokasi ketikan, serta setiap keputusan semakan — siapa yang membuatnya, apa yang berubah, dan sebabnya.",
    allEditors: "Semua penyelia", editTypeLabel: "Jenis pindaan", allTypes: "Semua jenis",
    typeTime: "Pindaan masa", typeLocation: "Pindaan lokasi", typeReview: "Keputusan semakan",
    noOverrides: "Tiada pindaan atau suntingan direkodkan lagi.",
    changeCol: "Perubahan", prevPage: "Sebelum", nextPage: "Seterusnya",
    punchEditsTab: "Pindaan ketikan", roleChangesTab: "Perubahan peranan",
    changedBy: "Diubah oleh", userCol: "Pengguna", createdAs: "Dicipta sebagai",
    noRoleChanges: "Tiada perubahan peranan direkodkan lagi.",
    weeklyTrend: "Trend mingguan", last12Weeks: "12 minggu terakhir",
    recalcButton: "Kira semula lewat/awal",
    recalcTitle: "Kira semula minit lewat & awal?",
    recalcBody: "Menyemak semula setiap ketikan dari 180 hari lepas berdasarkan syif dan tugasan semasa, dan membetulkan nilai lewat/awal yang salah (cth. ketikan syif malam dipadankan dengan syif siang). Selamat dijalankan pada bila-bila masa.",
    recalcDone: "ketikan diperbetulkan", recalcScanned: "disemak",
    mySummary: "Ringkasan saya",
    daysWorked: "Hari bekerja", onTimeDaysLabel: "Hari tepat masa", lateDaysLabel: "Hari lewat",
    onTimeStreakLabel: "Rentetan tepat masa", streakUnit: "hari tepat masa berturut-turut",
    streakPraise: "Syabas — teruskan!",
    streakStart: "Masuk kerja tepat pada masanya untuk memulakan rentetan.",
    noSummaryMonth: "Tiada ketikan direkodkan untuk bulan ini.",
    legendOnTime: "Tepat masa", legendLate: "Lewat", dayByDay: "Hari demi hari",
    totalMinEarly: "jumlah minit awal",
  },
};

const LanguageContext = createContext({ lang: "en", setLang: () => {}, t: (k) => k });

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem("rk_lang") || "en");
  const setLang = (l) => {
    localStorage.setItem("rk_lang", l);
    setLangState(l);
  };
  const value = useMemo(() => ({
    lang, setLang,
    t: (key) => STRINGS[lang]?.[key] ?? STRINGS.en[key] ?? key,
  }), [lang]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLang() {
  return useContext(LanguageContext);
}

export const OVERRIDE_REASONS = [
  "device_failure", "camera_unavailable", "gps_unavailable", "not_yet_enrolled",
  "appearance_changed", "poor_lighting", "system_outage", "other",
];

export function reasonLabel(t, code) {
  const map = {
    device_failure: "reasonDeviceFailure", camera_unavailable: "reasonCameraUnavailable",
    gps_unavailable: "reasonGpsUnavailable", not_yet_enrolled: "reasonNotYetEnrolled",
    appearance_changed: "reasonAppearanceChanged", poor_lighting: "reasonPoorLighting",
    system_outage: "reasonSystemOutage", other: "reasonOther",
  };
  return t(map[code] || "reasonOther");
}
